import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { Menu as MenuIcon, X, LogOut, ChevronDown, ChevronRight } from 'lucide-react';

// --- 1. SIDEBAR (Componente Visual) ---
const Sidebar = ({ routes, activeRoute, collapsed, setCollapsed, onNavigate, onLogout, logo }) => {
  const [expandedMenus, setExpandedMenus] = useState(new Set());
  const isMobile = window.innerWidth < 768;

  const toggleSubmenu = (routeName) => {
    const newExpanded = new Set(expandedMenus);
    if (newExpanded.has(routeName)) newExpanded.delete(routeName);
    else newExpanded.add(routeName);
    setExpandedMenus(newExpanded);
  };

  const handleMainRouteClick = (route) => {
    // Si tiene submenu, expandimos
    if (route.submenu && route.submenu.length > 0 && !collapsed) {
      toggleSubmenu(route.name);
      // Opcional: navegar al padre si se desea, aquí solo expande
    } else {
      onNavigate(route.path);
      if (isMobile) setCollapsed(true);
    }
  };

  return (
    <div className={`bg-gradient-to-b from-blue-800 to-blue-900 text-white h-screen fixed transition-all duration-300 shadow-xl z-20
        ${collapsed ? 'w-16' : 'w-64'} 
        ${isMobile && collapsed ? '-translate-x-full' : 'translate-x-0'}`}>
      
      {/* Header del Sidebar */}
      <div className="flex justify-between items-center p-4 border-b border-blue-700/50">
        {!collapsed && (
          <div className="flex justify-center items-center w-full py-4">
            <img src={logo} alt="Logo" className="max-w-[100%] mx-auto h-auto" style={{ maxHeight: "60px" }} />
          </div>
        )}
        <button
          className="p-2 rounded-lg hover:bg-blue-700/50 focus:outline-none transition-colors"
          onClick={() => setCollapsed(!collapsed)}
        >
          {collapsed ? <MenuIcon size={20} /> : <X size={20} />}
        </button>
      </div>

      {/* Lista de Rutas */}
      <div className="overflow-y-auto h-[calc(100vh-120px)]">
        <nav className="mt-6 px-3">
          <ul className="space-y-2">
            {routes.map((route) => (
              <li key={route.path || route.name}>
                <button
                  className={`flex items-center w-full p-3 rounded-lg transition-all duration-200 group relative
                    ${activeRoute.startsWith(route.path?.split('?')[0]) ? 'bg-blue-600 text-white shadow-lg' : 'text-blue-100 hover:bg-blue-700/50 hover:text-white'} 
                    ${collapsed ? 'justify-center' : 'justify-between'}`}
                  onClick={() => handleMainRouteClick(route)}
                >
                  <div className="flex items-center">
                    <span className="flex-shrink-0 relative">{route.icon}</span>
                    {!collapsed && <span className="ml-3 whitespace-nowrap font-medium">{route.name}</span>}
                  </div>
                  {/* Flecha Submenú */}
                  {!collapsed && route.submenu && route.submenu.length > 0 && (
                    <span className="transition-transform duration-200">
                      {expandedMenus.has(route.name) ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </span>
                  )}
                </button>

                {/* Renderizado de Submenú */}
                {route.submenu && route.submenu.length > 0 && !collapsed && (
                  <div className={`overflow-hidden transition-all duration-300 ${expandedMenus.has(route.name) ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'}`}>
                    <ul className="mt-2 ml-6 space-y-1 border-l-2 border-blue-600/30 pl-4">
                      {route.submenu.map((subItem) => (
                        <li key={subItem.path}>
                          <button
                            className={`flex items-center w-full p-2 rounded-md transition-all duration-200 
                              ${activeRoute === subItem.path ? 'bg-blue-500 text-white' : 'text-blue-200 hover:bg-blue-600/30 hover:text-white'}`}
                            onClick={() => {
                                onNavigate(subItem.path);
                                if (isMobile) setCollapsed(true);
                            }}
                          >
                            <span className="flex-shrink-0">{subItem.icon}</span>
                            <span className="ml-2 text-sm font-medium">{subItem.name}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </nav>
        
        {/* Botón Logout */}
        <div className="absolute bottom-0 w-full border-t border-blue-700/50 p-3">
          <button className={`flex items-center p-3 w-full text-blue-200 hover:bg-red-600/80 hover:text-white transition-all duration-200 rounded-lg ${collapsed ? 'justify-center' : 'justify-start'}`} onClick={onLogout}>
            <LogOut size={20} />
            {!collapsed && <span className="ml-3 font-medium">Cerrar Sesión</span>}
          </button>
        </div>
      </div>
    </div>
  );
};

// --- 2. TOPBARS (Móvil y Desktop) ---
const MobileTopBar = ({ setCollapsed, title }) => (
  <div className="md:hidden bg-gradient-to-r from-blue-800 to-blue-900 text-white p-3 sticky top-0 z-10 shadow-lg flex justify-between items-center">
    <div className="flex items-center">
      <div className="bg-white p-1 rounded-md mr-3"><span className="text-blue-800 font-bold text-xs">APP</span></div>
      <h1 className="font-bold text-lg">{title}</h1>
    </div>
    <button className="p-2 hover:bg-blue-700 rounded-lg" onClick={() => setCollapsed(false)}>
      <MenuIcon size={20} />
    </button>
  </div>
);

const DesktopTopBar = ({ user, title }) => {
  const [showMenu, setShowMenu] = useState(false);
  const navigate = useNavigate();
  return (
    <div className="hidden md:flex items-center justify-between bg-white p-4 border-b border-gray-200 shadow-sm sticky top-0 z-10">
      <div>
          <h1 className="font-bold text-xl text-gray-800">{title}</h1>
      </div>
      <div className="relative">
        <button onClick={() => setShowMenu(!showMenu)} className="flex items-center hover:bg-gray-50 p-2 rounded-lg transition-colors">
            {user?.fotoPerfil ? (
              <img src={user.fotoPerfil} alt="Perfil" className="w-8 h-8 rounded-full object-cover mr-2 border-2 border-blue-500" />
            ) : (
              <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-2">
                <span className="font-bold text-sm">{user?.nombrecompleto?.charAt(0) || 'U'}</span>
              </div>
            )}
            <span className="text-gray-700 font-medium">{user?.nombrecompleto || 'Usuario'}</span>
        </button>
        {/* Dropdown simple */}
        {showMenu && (
            <div className="absolute right-0 mt-2 w-48 bg-white border rounded-lg shadow-lg z-50">
              <button className="block w-full text-left px-4 py-3 text-sm hover:bg-gray-50" onClick={() => { setShowMenu(false); navigate('/dashboard/perfil'); }}>
                Mi Perfil
              </button>
            </div>
        )}
      </div>
    </div>
  );
};

// --- 3. LAYOUT PRINCIPAL ---
const DashboardLayout = ({ 
  routes = [], 
  user = {}, 
  onLogout,
  appTitle = "Panel de Control",
  customLogo
}) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const [activeRoute, setActiveRoute] = useState(location.pathname);

  // Sincronizar ruta activa
  useEffect(() => {
    setActiveRoute(location.pathname + location.search);
  }, [location]);

  // Manejo responsive automático
  useEffect(() => {
    const handleResize = () => { if (window.innerWidth < 768) setSidebarCollapsed(true); };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <MobileTopBar setCollapsed={setSidebarCollapsed} title={appTitle} />
  
      <div className="flex flex-1 overflow-hidden relative">
        <Sidebar
          routes={routes}
          activeRoute={activeRoute}
          collapsed={sidebarCollapsed}
          setCollapsed={setSidebarCollapsed}
          onNavigate={(path) => navigate(path)}
          onLogout={onLogout}
          logo={customLogo}
        />
        
        {/* Contenedor Principal */}
        <div 
          className={`flex-1 overflow-auto flex flex-col transition-all duration-300 ${sidebarCollapsed ? 'ml-0 md:ml-16' : 'ml-0 md:ml-64'}`}
          onClick={() => { if (window.innerWidth >= 768 && !sidebarCollapsed) setSidebarCollapsed(true); }}
        >
          <DesktopTopBar user={user} title={appTitle} />
          
          <div className="flex-1 p-4 md:p-6 bg-gray-50 min-h-0">
             {/* Aquí se inyecta el contenido de las rutas hijas */}
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;